import { useState, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { vibrate, openMail } from "../utils/platform";
import { Room } from "../types";
import "../styles/main.scss";

interface HomeScreenProps {
  rooms: Room[];
  loadingRooms: boolean;
  onFetchRooms: () => void;
  onJoinCode: (code: string) => void;
  onEnterRoom: (code: string) => void;
  // Camera props
  cameraSupported: boolean;
  cameraDenied: boolean;
  scannerError: string | null;
  scanning: boolean;
  videoRef: RefObject<HTMLVideoElement>;
  hideCamera?: boolean;
}

const MAIL_TO = "hello@blinkbli.com";

/**
 * HomeScreen Component - Figma Design
 * Clean QR scanner with bottom chatrooms bar
 */
export function HomeScreen({
  rooms,
  onEnterRoom,
  onJoinCode,
  cameraDenied,
  scannerError,
  scanning,
  videoRef,
  hideCamera = false,
}: HomeScreenProps) {
  const { t } = useTranslation();
  const [manualCode, setManualCode] = useState("");
  const [joiningManual, setJoiningManual] = useState(false);

  const handleManualJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualCode.trim() || !/^\d{8}$/.test(manualCode)) {
      alert(t("invalidCode"));
      return;
    }
    setJoiningManual(true);
    vibrate(10);
    try {
      await onJoinCode(manualCode);
      setManualCode("");
    } catch (err) {
      console.error("Failed to join room:", err);
    } finally {
      setJoiningManual(false);
    }
  };

  const handleCreateQR = () => {
    vibrate(5);
    openMail(MAIL_TO, "Create QR Code", "Hi, I'd like to create a QR code for my business/event.\n\nName:\nLocation:\n");
  };

  return (
    <div className="home-screen">
      {/* Main Content */}
      <div className="home-screen__content">
        {/* QR Scanner Box - Hidden when code is in URL */}
        {!hideCamera && (
          <>
            <div className="home-screen__scanner">
              <video
                ref={videoRef}
                playsInline
                muted
                className={`scanner__video ${cameraDenied ? 'scanner__video--hidden' : ''}`}
              />
              {!cameraDenied && scanning && (
                <div className="scanner__overlay">
                  <div className="scanner__frame" />
                </div>
              )}
              {scannerError && (
                <div className="scanner__error">
                  <p>{scannerError}</p>
                </div>
              )}
            </div>

            {/* Prompt Text */}
            <div className="home-screen__prompt">
              <p className="home-screen__prompt-title">
                {t("scanPrompt")}
              </p>
              <button 
                onClick={handleCreateQR}
                className="home-screen__prompt-link"
              >
                {t("createQR")}
              </button>
            </div>

            {/* QR Icon */}
            <div className="home-screen__qr-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 3h6v6H3V3zm2 2v2h2V5H5zm8-2h6v6h-6V3zm2 2v2h2V5h-2zM3 13h6v6H3v-6zm2 2v2h2v-2H5zm13-2h1v1h-1v-1zm-3 0h1v3h-1v-3zm1 4h3v1h-3v-1zm2-2h1v1h-1v-1zm1 0v-2h1v2h-1zm-1 3h2v1h-2v-1zm0-8h1v1h-1V8zm-3 3h1v1h-1v-1z"/>
              </svg>
            </div>
          </>
        )}

        {/* Manual Code Entry */}
        <div className="home-screen__manual-entry">
          <form onSubmit={handleManualJoin} className="manual-entry__form">
            <input
              type="text"
              placeholder={t("enterCode")}
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
              className="manual-entry__input"
              disabled={joiningManual}
              maxLength={8}
              pattern="\d{8}"
            />
            <button
              type="submit"
              disabled={!manualCode || manualCode.length !== 8 || joiningManual}
              className="manual-entry__button"
            >
              {joiningManual ? "..." : t("join")}
            </button>
          </form>
        </div>
      </div>

      {/* Bottom Chatrooms Bar */}
      <div className="home-screen__chatrooms-bar">
        <p className="home-screen__chatrooms-bar-title">{t("chatrooms")}</p>
        <div className="home-screen__chatrooms-bar-list">
          {rooms.length === 0 ? (
            <div className="home-screen__empty-rooms">
              {t("noRooms")}
            </div>
          ) : (
            rooms.map((room) => (
              <button
                key={room.code}
                className="home-screen__room-thumb"
                onClick={() => {
                  vibrate(10);
                  onEnterRoom(room.code);
                }}
                title={`Room ${room.code}`}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
